import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';

/** A single Jira issue assigned to the current user. */
export interface JiraTask {
  key: string;
  summary: string;
  url: string;
  /** Literal Jira status name, e.g. "Selected for Development". Never translated/bucketed. */
  status: string;
  /** Jira's own fixed statusCategory color ('blue-gray' | 'yellow' | 'green' | ''); not a per-board custom value. */
  statusCategoryColor: string;
  priority: string;
  priorityIconUrl: string;
  issueTypeName: string;
  issueTypeIconUrl: string;
  /** ISO-8601. */
  updated: string;
}

export interface OpenTasksResponse {
  tasks: JiraTask[];
  /** Deep link to Jira's own issue navigator, pre-filtered to the same query used for `tasks`. `null` when unavailable. */
  viewAllUrl: string | null;
}

export interface JiraMyTasksApi {
  /** Fetch the current user's open (non-Done) Jira issues, plus a deep link to see the rest in Jira. */
  getOpenTasks(): Promise<OpenTasksResponse>;
}

export const jiraMyTasksApiRef = createApiRef<JiraMyTasksApi>({
  id: 'plugin.jira-my-tasks.service',
});

export class JiraMyTasksClient implements JiraMyTasksApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  async getOpenTasks(): Promise<OpenTasksResponse> {
    const baseUrl = await this.discoveryApi.getBaseUrl('jira-my-tasks');
    const response = await this.fetchApi.fetch(`${baseUrl}/open`);

    if (!response.ok) {
      let message = `Failed to load Jira tasks (${response.status} ${response.statusText})`;
      try {
        const body = await response.json();
        if (typeof body?.error === 'string' && body.error) {
          message = body.error;
        }
      } catch {
        // Non-JSON error body — keep the status-based message.
      }
      throw new Error(message);
    }

    const data = (await response.json()) as OpenTasksResponse;
    return { tasks: data.tasks ?? [], viewAllUrl: data.viewAllUrl ?? null };
  }
}
